import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import DrawIcon from '@mui/icons-material/Draw';
import CustomButton from '../../../components/@extended/CustomButton';
import { useTheme } from '@emotion/react';
import { 
  useSystemAdminUpdateUserMutation,
  useLazySystemAdminGetUserDetailsQuery,
} from '../../../store/slices/apiSlice';
import { useDispatch } from 'react-redux';
import { openMessage } from '../../../store/slices/showMessageSlice';

// User type options
const USER_TYPE_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'data_manager', label: 'Data Manager' },
  { value: 'site_monitor', label: 'Site Monitor' },
  { value: 'sponsor_admin', label: 'Sponsor Admin' },
  { value: 'finance_admin', label: 'Finance Admin' },
];

// Signature preference options
const SIGNATURE_PREFERENCE_OPTIONS = [
  { value: 'draw', label: 'Draw' },
  { value: 'type', label: 'Type' },
  { value: 'upload', label: 'Upload' },
];

// Signature font options
const SIGNATURE_FONT_OPTIONS = [
  { value: 'Dancing Script', label: 'Dancing Script' },
  { value: 'Great Vibes', label: 'Great Vibes' },
  { value: 'Pacifico', label: 'Pacifico' },
  { value: 'Satisfy', label: 'Satisfy' },
  { value: 'Sacramento', label: 'Sacramento' },
];

const SectionHeader = ({ icon: Icon, title }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      <Icon sx={{ color: theme.palette.primary.main }} />
      <Typography variant="h6" fontWeight={600}>
        {title}
      </Typography>
    </Box>
  );
};

const UserPreferencesTab = ({ user, onUserUpdate }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  
  const [systemAdminUpdateUser, { isLoading: isUpdating }] = useSystemAdminUpdateUserMutation();
  const [fetchUserDetails, { isLoading: isFetching }] = useLazySystemAdminGetUserDetailsQuery();
  
  const [hasChanges, setHasChanges] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    // User Details
    firstName: '',
    lastName: '',
    address: '',
    contactNumber: '',
    active: true,
    // User Preferences
    userType: '',
    multiFactorAuth: false,
    signaturePreference: 'draw',
    signatureFont: 'Dancing Script',
  });

  // Fetch user details on mount
  useEffect(() => {
    if (user?.id) {
      loadUserDetails();
    }
  }, [user?.id]);

  const loadUserDetails = async () => {
    try {
      const result = await fetchUserDetails({ userId: user.id }).unwrap();
      setUserDetails(result);
      
      // Populate form with fetched data
      setFormData({
        firstName: result?.firstName || '',
        lastName: result?.lastName || '',
        address: result?.address || '',
        contactNumber: result?.contactNumber || '',
        active: result?.active ?? true,
        userType: result?.userMetaInfo?.userType || '',
        multiFactorAuth: result?.userMetaInfo?.multiFactorAuth || false,
        signaturePreference: result?.userMetaInfo?.signaturePreference || 'draw',
        signatureFont: result?.userMetaInfo?.signatureFont || 'Dancing Script',
      });
    } catch (error) {
      dispatch(openMessage({
        messageSeverity: 'error',
        message: error?.data?.message || 'Failed to fetch user details',
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      // Build update payload - only include changed fields
      const updatePayload = {};
      
      // User details
      if (formData.firstName !== (userDetails?.firstName || '')) {
        updatePayload.firstName = formData.firstName;
      }
      if (formData.lastName !== (userDetails?.lastName || '')) {
        updatePayload.lastName = formData.lastName;
      }
      if (formData.address !== (userDetails?.address || '')) {
        updatePayload.address = formData.address;
      }
      if (formData.contactNumber !== (userDetails?.contactNumber || '')) {
        updatePayload.contactNumber = formData.contactNumber;
      }
      if (formData.active !== (userDetails?.active ?? true)) {
        updatePayload.active = formData.active;
      }
      
      // User preferences
      if (formData.userType !== (userDetails?.userMetaInfo?.userType || '')) {
        updatePayload.userType = formData.userType || null;
      }
      if (formData.multiFactorAuth !== (userDetails?.userMetaInfo?.multiFactorAuth || false)) {
        updatePayload.multiFactorAuth = formData.multiFactorAuth;
      }
      if (formData.signaturePreference !== (userDetails?.userMetaInfo?.signaturePreference || 'draw')) {
        updatePayload.signaturePreference = formData.signaturePreference;
      }
      if (formData.signatureFont !== (userDetails?.userMetaInfo?.signatureFont || 'Dancing Script')) {
        updatePayload.signatureFont = formData.signatureFont;
      }

      if (Object.keys(updatePayload).length === 0) {
        dispatch(openMessage({
          messageSeverity: 'info',
          message: 'No changes to save',
        }));
        return;
      }

      await systemAdminUpdateUser({
        userId: user.id,
        body: updatePayload,
      }).unwrap();

      dispatch(openMessage({
        messageSeverity: 'success',
        message: 'User details and preferences updated successfully',
      }));

      setHasChanges(false);
      
      // Reload user details
      await loadUserDetails();
      
      if (onUserUpdate) {
        onUserUpdate();
      }
    } catch (error) {
      dispatch(openMessage({
        messageSeverity: 'error',
        message: error?.data?.message || 'Failed to update user details',
      }));
    }
  };

  const handleReset = () => {
    if (userDetails) {
      setFormData({
        firstName: userDetails?.firstName || '',
        lastName: userDetails?.lastName || '',
        address: userDetails?.address || '',
        contactNumber: userDetails?.contactNumber || '',
        active: userDetails?.active ?? true,
        userType: userDetails?.userMetaInfo?.userType || '',
        multiFactorAuth: userDetails?.userMetaInfo?.multiFactorAuth || false,
        signaturePreference: userDetails?.userMetaInfo?.signaturePreference || 'draw',
        signatureFont: userDetails?.userMetaInfo?.signatureFont || 'Dancing Script',
      });
      setHasChanges(false);
    }
  };

  if (isFetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, pt: 0 }}>
      {/* User Details Section */}
      <SectionHeader icon={PersonIcon} title="User Details" />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Contact Number"
            value={formData.contactNumber}
            onChange={(e) => handleInputChange('contactNumber', e.target.value)}
            size="small"
            helperText="Changing this will reset phone verification"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Address"
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(e) => handleInputChange('active', e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>Account Active</Typography>
                <Chip 
                  label={formData.active ? 'Active' : 'Inactive'} 
                  color={formData.active ? 'success' : 'error'} 
                  size="small" 
                />
              </Box>
            }
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* User Type Section */}
      <SectionHeader icon={SecurityIcon} title="User Type & Preferences" />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>User Type</InputLabel>
            <Select
              value={formData.userType}
              label="User Type"
              onChange={(e) => handleInputChange('userType', e.target.value)}
            >
              {USER_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.multiFactorAuth}
                onChange={(e) => handleInputChange('multiFactorAuth', e.target.checked)}
                color="primary"
              />
            }
            label="Multi-Factor Authentication"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Signature Preferences Section */}
      <SectionHeader icon={DrawIcon} title="Signature Preferences" />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Signature Method</InputLabel>
            <Select
              value={formData.signaturePreference}
              label="Signature Method"
              onChange={(e) => handleInputChange('signaturePreference', e.target.value)}
            >
              {SIGNATURE_PREFERENCE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Signature Font</InputLabel>
            <Select
              value={formData.signatureFont}
              label="Signature Font"
              onChange={(e) => handleInputChange('signatureFont', e.target.value)}
              disabled={formData.signaturePreference !== 'type'}
            >
              {SIGNATURE_FONT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Typography sx={{ fontFamily: option.value }}>{option.label}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {formData.signaturePreference === 'type' && formData.signatureFont && (
          <Grid item xs={12}>
            <Box 
              sx={{ 
                p: 2, 
                border: `1px solid ${theme.palette.divider}`, 
                borderRadius: 1,
                backgroundColor: theme.palette.grey[50],
              }}
            >
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Signature Preview
              </Typography>
              <Typography 
                sx={{ 
                  fontFamily: formData.signatureFont, 
                  fontSize: '2rem',
                  color: theme.palette.text.primary,
                }}
              >
                {formData.firstName} {formData.lastName}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Action Buttons */}
      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <CustomButton
          variant="outlined"
          onClick={handleReset}
          disabled={isUpdating || !hasChanges}
        >
          Reset
        </CustomButton>
        <CustomButton
          variant="contained"
          onClick={handleSave}
          disabled={isUpdating || !hasChanges}
          startIcon={isUpdating && <CircularProgress size={16} color="inherit" />}
        >
          {isUpdating ? 'Saving...' : 'Save Changes'}
        </CustomButton>
      </Box>

      {hasChanges && (
        <Alert severity="info" sx={{ mt: 2 }}>
          You have unsaved changes. Click "Save Changes" to apply them.
        </Alert>
      )}
    </Box>
  );
};

export default UserPreferencesTab;
